import {Component, DestroyRef, computed, effect, input, signal, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed, rxResource} from '@angular/core/rxjs-interop';
import {Dialog} from '@angular/cdk/dialog';
import {
  WaButtonDirective,
  WaCalloutDirective,
  WaCardDirective,
  WaIconDirective,
  WaOptionComponent,
  WaSelectWrapperComponent,
  WaTabComponent,
  WaTabGroupComponent,
  WaTabPanelComponent,
  WaToastService
} from 'angular-awesome';
import {lastValueFrom} from 'rxjs';

import {DEFAULT_PARAMETER_SET, POLL_INTERVAL} from '../../../api/constants';
import {ParameterSet, ParameterSetRequest, ParameterSetResponse} from '../../../api/models/parameter';
import {SolutionQuality} from '../../../api/models/summary-data';
import {Survey} from '../../../api/models/survey';
import {User} from '../../../api/models/user';
import {ParameterSetService} from '../../../api/services/parameter.service';
import {SurveyService} from '../../../api/services/survey.service';
import {
  ConfirmationDialogComponent
} from '../../general-purpose/dialogs/confirmation-dialog/confirmation-dialog.component';
import {
  ParameterNameDialogData,
  ParametersCreateComponent
} from './parameters-create/parameters-create.component';
import {SliderComponent} from './slider/slider.component';
import {ExportPanelComponent} from '../export-panel/export-panel.component';

@Component({
  selector: 'app-parameter-set-editor',
  imports: [
    SliderComponent,
    WaButtonDirective,
    WaCalloutDirective,
    WaCardDirective,
    WaIconDirective,
    WaOptionComponent,
    WaSelectWrapperComponent,
    WaTabComponent,
    WaTabGroupComponent,
    WaTabPanelComponent,
    ExportPanelComponent
  ],
  templateUrl: './parameter-set-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './parameter-set-editor.component.scss'
})
export class ParameterSetEditorComponent {

  readonly user = input<User | null>(null);
  readonly survey = input<Survey | null>(null);

  readonly parameterSets = rxResource({
    stream: () => this.parameterSetService.getAll()
  });
  readonly displayedParameterSets = computed(() => {
    if (!this.parameterSets.hasValue()) return [];
    const sets = this.parameterSets.value();
    return sets.length ? sets : [this.fallbackParameterSet];
  });

  readonly selectedParameterSet = signal<ParameterSetResponse | null>(null);
  readonly defaultParameterSetId = signal<number | null>(null);
  readonly selectedParameterSetLabel = signal('');
  readonly changed = signal(false);
  readonly processing = signal(false);
  readonly solutionData = signal<SolutionQuality>({
    iis: [],
    latest_failed: false,
    supervision: [],
    correction: []
  });
  readonly supervisionSliders = signal<{key: string; value: number}[]>([]);
  readonly correctionSliders = signal<{key: string; value: number}[]>([]);

  private readonly draft = signal<ParameterSet | null>(null);
  private pendingSelectionId: number | null = null;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly fallbackParameterSet: ParameterSetResponse = {
    id: 0,
    owner_id: 0,
    ...DEFAULT_PARAMETER_SET,
    name: 'DEFAULT'
  };

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly dialog: Dialog,
    private readonly parameterSetService: ParameterSetService,
    private readonly surveyService: SurveyService,
    private readonly toastService: WaToastService
  ) {
    this.destroyRef.onDestroy(() => this.stopPolling());

    effect(() => {
      if (!this.parameterSets.hasValue() || this.parameterSets.isLoading()) return;

      const sets = this.parameterSets.value();
      if (!sets.length) {
        this.defaultParameterSetId.set(null);
        if (this.selectedParameterSet()?.id !== this.fallbackParameterSet.id) {
          this.applySelection(this.fallbackParameterSet);
        }
        return;
      }

      const pendingId = this.pendingSelectionId;
      if (pendingId !== null) {
        const pending = sets.find(parameterSet => parameterSet.id === pendingId) ?? null;
        this.pendingSelectionId = null;
        if (pending) {
          this.selectParameterSet(pending, true);
          return;
        }
      }

      const selected = this.selectedParameterSet();
      if (selected && selected.id !== this.fallbackParameterSet.id) {
        const refreshed = sets.find(parameterSet => parameterSet.id === selected.id) ?? null;
        if (refreshed) {
          this.selectedParameterSet.set(refreshed);
          this.updateSelectedLabel();
          return;
        }
      }

      const defaultId = this.defaultParameterSetId();
      if (defaultId !== null) {
        const defaultSet = sets.find(parameterSet => parameterSet.id === defaultId) ?? null;
        if (defaultSet) {
          this.selectParameterSet(defaultSet, true);
          return;
        }
      }

      if (selected?.id === this.fallbackParameterSet.id) {
        this.selectParameterSet(sets[0], true);
      }
    });

    effect(() => {
      const survey = this.survey();
      if (!survey) return;
      void this.loadDefaultParameterSet(survey.id);
      void this.refreshSolutionSummary(survey.id);
    });
  }

  onParameterSetChange(rawValue: unknown): void {
    const rawId = this.extractSelectValue(rawValue);

    if (rawId === null || rawId === undefined || rawId === '') {
      this.clearSelection();
      return;
    }

    const id = Number(rawId);
    if (Number.isNaN(id) || !this.parameterSets.hasValue()) return;

    const selected = this.displayedParameterSets().find(parameterSet => parameterSet.id === id) ?? null;
    if (selected) {
      this.selectParameterSet(selected, selected.id !== this.fallbackParameterSet.id);
    }
  }

  onSupervisionParamChange(name: string, value: number): void {
    this.updateDraftParameter('supervision', name, value);
  }

  onCorrectionParamChange(name: string, value: number): void {
    this.updateDraftParameter('correction', name, value);
  }

  canUpdate(): boolean {
    const selected = this.selectedParameterSet();
    const user = this.user();
    return !!selected && !!user && selected.owner_id === user.id && this.changed();
  }

  canSaveAsNew(): boolean {
    const selected = this.selectedParameterSet();
    return !!selected && (this.changed() || selected.id === this.fallbackParameterSet.id);
  }

  canRemove(): boolean {
    const selected = this.selectedParameterSet();
    const user = this.user();
    return !!selected && !!user && selected.owner_id === user.id;
  }

  canOptimize(): boolean {
    return !!this.selectedParameterSet() && !this.changed() && !this.processing();
  }

  async update(): Promise<void> {
    const selected = this.selectedParameterSet();
    if (!selected || !this.canUpdate()) return;

    try {
      const updated = await lastValueFrom(
        this.parameterSetService.update(selected.id, this.currentRequest(selected.name))
      );
      await this.refreshAndReselect(updated.id);
      await this.makeDefault(updated.id);
      this.toastService.success('Parameter set updated successfully.');
    } catch {
      this.toastService.danger('Failed to update parameter set. Please try again.');
    }
  }

  saveAsNew(): void {
    const selected = this.selectedParameterSet();
    if (!selected || !this.canSaveAsNew()) return;

    const data: ParameterNameDialogData = {
      suggestedName: `${selected.name} (modified)`,
      existingNames: this.displayedParameterSets().map(parameterSet => parameterSet.name)
    };

    const ref = this.dialog.open<string>(ParametersCreateComponent, {
      disableClose: true,
      data
    });

    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async name => {
      if (!name?.trim()) return;

      try {
        const created = await lastValueFrom(this.parameterSetService.create(this.currentRequest(name.trim())));
        await this.refreshAndReselect(created.id);
        await this.makeDefault(created.id);
        this.toastService.success('Parameter set saved as new successfully.');
      } catch {
        this.toastService.danger('Failed to save parameter set as new. Please try again.');
      }
    });
  }

  remove(): void {
    const selected = this.selectedParameterSet();
    const survey = this.survey();

    if (!selected || !survey || !this.canRemove()) return;

    const ref = this.dialog.open<boolean>(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        title: 'Confirm deletion',
        message: `Are you sure that you want to delete parameter set ${selected.name}?`,
        cancel_button: true
      }
    });

    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async confirmed => {
      if (!confirmed) return;

      try {
        await lastValueFrom(this.parameterSetService.delete(selected.id));
        this.clearSelection();
        this.defaultParameterSetId.set(null);
        this.parameterSets.reload();
        await this.loadDefaultParameterSet(survey.id);
        this.toastService.success(`Parameter set "${selected.name}" deleted.`);
      } catch {
        this.toastService.danger('Failed to delete parameter set. Please try again.');
      }
    });
  }

  async solve(): Promise<void> {
    const survey = this.survey();
    const selected = this.selectedParameterSet();

    if (!survey || !selected || this.changed() || this.processing()) return;

    try {
      this.processing.set(true);
      await this.surveyService.solve(survey.id, this.currentRequest(selected.name));
      this.startPolling(survey.id, true);
    } catch (error: any) {
      this.processing.set(false);
      const msg = error?.error?.detail || 'Could not solve ILP.';
      this.toastService.danger(msg);
    }
  }

  async abort(): Promise<void> {
    const survey = this.survey();
    if (!survey || !this.processing()) return;

    try {
      await this.surveyService.abort(survey.id);
      this.stopPolling();
      this.processing.set(false);
      this.toastService.success('Optimization aborted.');
    } catch (error: any) {
      const msg = error?.error?.detail || 'Could not abort computation.';
      this.toastService.danger(msg);
    }
  }

  isDefault(parameterSet: ParameterSetResponse): boolean {
    return parameterSet.id === this.defaultParameterSetId() || parameterSet.id === this.fallbackParameterSet.id;
  }

  isFallbackParameterSet(parameterSet: ParameterSetResponse): boolean {
    return parameterSet.id === this.fallbackParameterSet.id;
  }

  isFallbackSelected(): boolean {
    return this.selectedParameterSet()?.id === this.fallbackParameterSet.id;
  }

  selectedValue(): string | null {
    const selected = this.selectedParameterSet();
    return selected ? String(selected.id) : null;
  }

  private extractSelectValue(rawValue: unknown): string | number | null {
    if (rawValue === null || rawValue === undefined) return null;
    if (typeof rawValue === 'string' || typeof rawValue === 'number') return rawValue;

    const eventLike = rawValue as {
      detail?: { value?: string | number | null };
      target?: { value?: string | number | null };
    };

    if (eventLike.detail?.value !== undefined) return eventLike.detail.value;
    if (eventLike.target?.value !== undefined) return eventLike.target.value;
    return null;
  }

  private async loadDefaultParameterSet(surveyId: number): Promise<void> {
    try {
      const defaultParameterSet = await lastValueFrom(this.surveyService.getDefaultParameters(surveyId));
      this.defaultParameterSetId.set(defaultParameterSet?.id ?? null);

      if (defaultParameterSet && !this.selectedParameterSet()) {
        if (this.parameterSets.hasValue() && !this.parameterSets.isLoading()) {
          const found = this.parameterSets.value().find(parameterSet => parameterSet.id === defaultParameterSet.id) ?? defaultParameterSet;
          this.selectParameterSet(found, true);
        } else {
          this.pendingSelectionId = defaultParameterSet.id;
        }
      }
    } catch {
      this.toastService.danger('Could not fetch default parameters.');
    }
  }

  private async refreshSolutionSummary(surveyId: number): Promise<void> {
    try {
      const info = await this.surveyService.getSolutionSummary(surveyId);
      this.solutionData.set(info);
      this.processing.set(false);
    } catch (error: any) {
      if (error?.status === 425) {
        this.processing.set(true);
        this.startPolling(surveyId, false);
      }
    }
  }

  private selectParameterSet(parameterSet: ParameterSetResponse, fetchFullDetails: boolean): void {
    if (fetchFullDetails) {
      this.parameterSetService.get(parameterSet.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: loaded => this.applySelection(loaded),
          error: () => this.toastService.danger('Failed to load parameter set values. Please try again.')
        });
      return;
    }

    this.applySelection(parameterSet);
  }

  private applySelection(parameterSet: ParameterSetResponse): void {
    this.selectedParameterSet.set(parameterSet);
    this.draft.set(ParameterSet.from(parameterSet));
    this.supervisionSliders.set(
      Object.entries(parameterSet.supervision).map(([key, value]) => ({key, value}))
    );
    this.correctionSliders.set(
      Object.entries(parameterSet.correction).map(([key, value]) => ({key, value}))
    );
    this.changed.set(false);
    this.updateSelectedLabel();
    if (parameterSet.id !== this.fallbackParameterSet.id) {
      void this.makeDefault(parameterSet.id, false);
    }
  }

  private clearSelection(): void {
    this.selectedParameterSet.set(null);
    this.selectedParameterSetLabel.set('');
    this.draft.set(null);
    this.changed.set(false);
    this.supervisionSliders.set([]);
    this.correctionSliders.set([]);
  }

  private updateDraftParameter(kind: 'supervision' | 'correction', name: string, value: number): void {
    const draft = this.draft();
    if (!draft) return;

    draft[kind].set(name, value);
    this.changed.set(true);
    this.updateSelectedLabel();
  }

  private updateSelectedLabel(): void {
    const selected = this.selectedParameterSet();
    this.selectedParameterSetLabel.set(selected ? `${selected.name}${this.changed() ? ' *' : ''}` : '');
  }

  private currentRequest(nameOverride?: string): ParameterSetRequest {
    const draft = this.draft();
    if (!draft) {
      throw new Error('No parameter set selected.');
    }
    return draft.toRequest(nameOverride);
  }

  private async makeDefault(parameterSetId: number, notify: boolean = false): Promise<void> {
    const survey = this.survey();
    if (!survey) return;

    try {
      const response = await lastValueFrom(this.surveyService.setDefaultParameters(survey.id, parameterSetId));
      this.defaultParameterSetId.set(response.parameters_id);
      if (notify) {
        this.toastService.success('Default parameter set updated.');
      }
    } catch {
      this.toastService.danger('Could not set default parameter set.');
    }
  }

  private async refreshAndReselect(parameterSetId: number): Promise<void> {
    this.pendingSelectionId = parameterSetId;
    this.parameterSets.reload();
  }

  private startPolling(surveyId: number, notifyOnCompletion: boolean): void {
    this.stopPolling();
    this.pollIntervalId = setInterval(async () => {
      try {
        const info = await this.surveyService.getSolutionSummary(surveyId);
        this.solutionData.set(info);
        this.processing.set(false);
        this.stopPolling();

        if (notifyOnCompletion) {
          if (info.latest_failed) {
            this.toastService.danger('Optimization failed!');
          } else {
            this.toastService.success('Optimization completed!');
          }
        }
      } catch (error: any) {
        if (error?.status === 425) {
          this.processing.set(true);
          return;
        }

        this.processing.set(false);
        this.stopPolling();
      }
    }, POLL_INTERVAL);
  }

  private stopPolling(): void {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

}

